import { useEffect, useState, useRef } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../../basic/Button";
import { trellisPalette } from "../../../custom/analytics/palettes";
import { css } from "../../../../../styled-system/css";
import { Flex } from "../../../../../styled-system/jsx";
import useWizard from "./useWizard";
import FormWizardPagination from "./FormWizardPagination";

import withMediaQuery from "../../../../hocs/withMediaQuery";

const stepIcon = css({
  marginRight: ".5rem",
  fontWeight: 500,
});

const wizardStepContainer = css.raw({
  boxSizing: "border-box",
  borderRight: "1px solid #DDD",
  marginRight: "xlarge",
  position: "relative",
  maxHeight: "80vh",
  maxWidth: "240px",
  width: "100%",
  "2xl": { flex: 1 },
  "@media (max-width:1279px)": {
    minHeight: "0",
    borderRight: 0,
    maxWidth: "100%",
    margin: "auto",
  },
});

const fullPageWizardStepContainer = css.raw({
  maxHeight: "none",
  marginTop: "-106px",
  marginBottom: "5px",
});

const wizardStepContainerInner = css({
  listStyleType: "none",
  "@media (max-width:1279px)": {
    display: "flex",
    justifyContent: "space-around",
  },
  "@media (max-width:600px)": {
    flexDirection: "column",
  },
});

const parentStep = css({
  fontSize: "16px",
  fontWeight: 500,
  "@media (max-width:1279px)": {
    fontSize: "16px",
  },
});

const substeps = css({
  listStyleType: "none",
  paddingLeft: ".5rem",
});

const substepStyle = css({
  fontSize: "14px",
  fontWeight: 500,
  paddingLeft: "small",
  paddingTop: "small",
  "@media (max-width:1279px)": {
    fontSize: "14px",
  },
});

const formContainer = css({
  position: "relative",
  height: "80vh",
  overflowY: "auto",
  overflowX: "clip",
  paddingBottom: "50px",
  paddingRight: "1.5rem",
  flex: 1,
  minWidth: 0,
  "@media (max-width:1279px)": {
    flex: "unset",
    width: "100%",
    maxWidth: "100%",
    height: "auto",
  },
});

const scrollBar = css({
  _scrollbarTrack: {
    marginBottom: "2rem",
    backgroundColor: "rgb(206, 212, 218)",
    borderRadius: "2px",
  },
  _scrollbar: {
    width: "0.45rem",
    maxHeight: "12px",
    borderRadius: "2px",
  },
  _scrollbarThumb: {
    backgroundColor: "trellisPalette.3",
    width: "0.45rem",
    borderRadius: "2px",
  },
});

const FormWizard = ({
  updateStep,
  editStep,
  setEditStep,
  editSubstep,
  errors,
  isValid,
  isModal,
  closeModal,
  state = {},
  onCreate,
  onUpdate,
  onCancel,
  showCancelButton,
  exitButton,
  onExit,
  hidePreviousButtonVisibility,
  clearForm,
  fieldsToValidate,
  setFieldsToValidate,
  setReadyForStep,
  formSteps,
  assessFormSteps,
  formToRender,
  formStepDependencies,
  completeText,
  isChild,
  saveChild,
  childStep,
  reset,
  autoExpandOn,
  autoExpandStep,
  formProgressRefs,
  mediaQuery,
  wizardContext,
  getExemptNavPaths = () => {},
  isProcessing = false,
}) => {
  const { t } = useTranslation(["advertising", "common"]);
  let { onNext, onPrevious, step, setStep, steps, substep, setSubstep, highestStepVisited, highestSubstepVisited } =
    useWizard(formSteps, assessFormSteps, formStepDependencies);

  steps = steps.filter((step) => !step.isHidden);
  // Below tracks validations for current step/substep, needed to trigger success animation
  const [prospectiveFieldsToValidate, setProspectiveFieldsToValidate] = useState([]);
  const [scrollDetected, setScrollDetected] = useState(false);
  const formContainerRef = useRef();

  const onSubmit = async () => {
    if (isValid) {
      if (state.id) {
        await onUpdate(state);
        if (state.canHaveChildren) {
          // onParentUpdate();
        }

        isModal && closeModal();
      } else {
        await onCreate(state);
        clearForm && clearForm();
        isModal && closeModal();
      }

      reset();
    }
  };

  const onScroll = () => {
    if (formContainerRef.current) {
      const { scrollTop } = formContainerRef.current;
      if (scrollTop) {
        setScrollDetected(true);
      } else {
        setScrollDetected(false);
      }
    }
  };

  useEffect(() => {
    if (editStep !== null && !getExemptNavPaths(state, editStep, editSubstep)) {
      updateStep(editStep, editSubstep ?? 0);
      setStep(editStep);
    } else {
      updateStep(step, substep);
    }

    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [step, substep, updateStep, editStep, editSubstep, setStep, getExemptNavPaths]);

  useEffect(() => {
    // Below decides when to render next step in form once all validations passed
    // TODO: decide on behavior when user goes back and makes previous step fields invalid
    const readyToProceed =
      prospectiveFieldsToValidate?.length > 0 &&
      !Object?.keys(errors).some((error) => prospectiveFieldsToValidate?.includes(error));

    // Only auto-expand to a step that exists; consumers derive autoExpandStep from scroll position
    // and can hand us an index with no step behind it.
    const autoExpandTarget = steps[autoExpandStep] ? autoExpandStep : null;

    if (
      autoExpandTarget !== null &&
      ((readyToProceed && autoExpandOn) || (step && autoExpandOn && autoExpandTarget < step))
    ) {
      updateStep(autoExpandTarget);

      if (step !== autoExpandTarget) {
        setFieldsToValidate((prevValidations) => [...prevValidations, ...steps[autoExpandTarget].validations]);
      }

      setStep(autoExpandTarget);

      setReadyForStep && setReadyForStep(readyToProceed ? step + 1 : step);
    } else {
      setReadyForStep && autoExpandOn && setReadyForStep(readyToProceed ? step + 1 : step);
      readyToProceed && autoExpandOn && updateStep(step + 1);
    }
  }, [
    prospectiveFieldsToValidate,
    errors,
    setReadyForStep,
    step,
    setStep,
    updateStep,
    autoExpandOn,
    autoExpandStep,
    fieldsToValidate,
    setFieldsToValidate,
    steps,
    substep,
  ]);

  useEffect(() => {
    // Override Bootstrap's modal-body default padding without impacting all other modals
    if (document.querySelector(".modal-body")) {
      document.querySelector(".modal-body").style.paddingLeft = "0";
      document.querySelector(".modal-body").style.paddingBottom = "0";
      document.querySelector(".modal-body").style.paddingRight = "0";
    }
  }, [steps]);

  // Below is for checking validations without displaying error message to trigger next button animation/highlight
  useEffect(() => {
    setProspectiveFieldsToValidate(
      steps[step]?.substeps?.length > 0 ? steps[step]?.substeps[substep]?.validations : steps[step]?.validations,
    );
  }, [step, steps, substep, childStep]);

  return (
    <Flex wrap="wrap" translate="no">
      {(!state.hasParent || (state.hasParent && state.id) || state.newChild) && (
        <div className={css(wizardStepContainer, autoExpandOn && fullPageWizardStepContainer)}>
          <ul className={`${wizardStepContainerInner} p-0`}>
            {steps
              ?.filter((step) => !step.isHidden)
              ?.map((label, parentIndex) => (
                <li
                  key={`${label}-${parentIndex}`}
                  style={{
                    backgroundColor: parentIndex === step ? "#62407F" : "inherit",
                    padding: "1rem",
                  }}
                >
                  <span
                    className={parentStep}
                    style={{
                      cursor:
                        parentIndex <= highestStepVisited || (autoExpandOn && parentIndex <= autoExpandStep) ?
                          "pointer"
                        : "inherit",
                      color: parentIndex === step ? "#FFF" : "#999",
                      paddingLeft: parentIndex > step ? "2.3rem" : "0",
                    }}
                    onClick={() => {
                      if (autoExpandOn && parentIndex <= autoExpandStep) {
                        if (formProgressRefs[parentIndex].current) {
                          formProgressRefs[parentIndex].current.scrollIntoView({
                            behavior: "smooth",
                            block: "nearest",
                          });
                        }
                        return;
                      }

                      if (parentIndex <= highestStepVisited) {
                        updateStep(parentIndex, null);
                        setStep(parentIndex);

                        setSubstep(0);
                      }
                    }}
                  >
                    {parentIndex < step && <i className={`fa fa-check ${stepIcon}`} />}
                    {parentIndex === step && <i className={`fa fa-solid fa-caret-right ${stepIcon}`} />}
                    <span>
                      {parentIndex + 1}. <span>{label?.title ? t(label.title) : ""}</span>
                    </span>
                  </span>
                  {label?.substeps && parentIndex === step && (
                    <ul className={substeps}>
                      {label?.substeps?.map((sub, index) => {
                        if (index === 0) {
                          return null;
                        }

                        return (
                          <li
                            key={`${sub}-${index}`}
                            data-att={index}
                            className={substepStyle}
                            style={{
                              color: parentIndex === step ? "#FFF" : "#999",
                              cursor:
                                parentIndex <= highestStepVisited && index <= highestSubstepVisited ?
                                  "pointer"
                                : "inherit",
                              paddingLeft: 0,
                            }}
                            onClick={() => {
                              if (autoExpandOn) return;

                              if (parentIndex <= highestStepVisited && index <= highestSubstepVisited) {
                                setStep(parentIndex);
                                updateStep(parentIndex);
                                setSubstep(index);
                              }
                            }}
                          >
                            {index < childStep + 1 || index < substep ?
                              <i className={`fa fa-check ${stepIcon}`} />
                            : index === childStep + 1 || (index === substep && !childStep) ?
                              <i className={`fa fa-solid fa-caret-right ${stepIcon}`}></i>
                            : null}
                            <span>{sub.title ? t(sub.title) : ""}</span>
                          </li>
                        );
                      })}
                    </ul>
                  )}
                </li>
              ))}
          </ul>
          <FormWizardPagination
            readyToProceed={
              !Object?.keys(errors).some((error) => prospectiveFieldsToValidate?.includes(error)) && !isProcessing
            }
            isProcessing={isProcessing}
            isModal={isModal}
            onPrevious={() => {
              if (autoExpandOn && step) {
                if (formProgressRefs[step - 1].current) {
                  formProgressRefs[step - 1].current.scrollIntoView({
                    behavior: "smooth",
                    block: "nearest",
                  });
                }
              }

              if (showCancelButton && step === 0) {
                onCancel();
              }

              if (step > 0) {
                onPrevious();
              } else {
                onCancel();
              }
            }}
            onPreviousText={
              step === 0 ? t(($) => $.common.actions.Cancel)
              : childStep === 1 ?
                `${step}. ${steps[step - 1]?.title ? t(steps[step - 1].title) : ""}`
              : steps[step]?.substeps?.[substep - 1] ?
                `${step + 1}${String.fromCharCode(substep - 1 + 65).toLowerCase()}.  ${
                  steps[step]?.substeps?.[substep - 1]?.title ? t(steps[step].substeps[substep - 1].title) : ""
                }`
              : `${step}. ${steps[step - 1]?.title ? t(steps[step - 1].title) : ""}`
            }
            hidePreviousButtonVisibility={(step === 0 && !showCancelButton) || hidePreviousButtonVisibility}
            onNext={async () => {
              if (autoExpandOn && step < steps.length - 1) {
                if (formProgressRefs[step + 1].current) {
                  formProgressRefs[step + 1].current.scrollIntoView({
                    behavior: "smooth",
                    block: "nearest",
                  });
                }
              }

              if (editStep !== null) {
                setStep(editStep);
                setSubstep(null);
                setEditStep(null);
              }

              if (step === steps.filter((s) => !s.isHidden)?.length - 1) {
                onSubmit();
              } else {
                setFieldsToValidate(
                  steps[step]?.substeps?.length > 0 ?
                    steps[step]?.substeps[substep]?.validations
                  : steps[step]?.validations,
                );

                if (
                  !Object?.keys(errors).some((error) =>
                    steps[step]?.substeps?.length > 0 ?
                      steps[step]?.substeps[substep]?.validations?.includes(error)
                    : steps[step]?.validations?.includes(error),
                  )
                ) {
                  if (saveChild) {
                    updateStep(2, 0);
                    setStep(2);
                    setSubstep(0);
                    return;
                  }

                  if (
                    !state.newLandingPage &&
                    state.storeSpotlight &&
                    step === 2 &&
                    substep === 0 &&
                    state.renderAdvancedOptions
                  ) {
                    updateStep(2, 1);
                    setStep(2);
                    setSubstep(1);
                    return;
                  }

                  onNext();
                }
              }
            }}
            onNextText={
              saveChild ? t(($) => $.common.actions.Save)
              : step === steps.length - 1 ?
                completeText
              : steps[step]?.substeps?.[substep + 1] ?
                `${step + 1}${String.fromCharCode(substep + 1 + 65).toLowerCase()}. ${
                  steps[step]?.substeps?.[substep + 1]?.title ? t(steps[step].substeps[substep + 1].title) : ""
                }`
              : `${step + 2}. ${steps[step + 1]?.title ? t(steps[step + 1].title) : ""}`
            }
            hideButtonVisibility={isChild}
            isChild={isChild}
            showCancelButton={showCancelButton}
            onCancel={onCancel}
            hidePagination={autoExpandOn && mediaQuery}
            exitButton={exitButton}
            onExit={onExit}
            hideCancelButton={wizardContext === "media-plan"}
          />
        </div>
      )}
      <div
        className={`${formContainer} ${scrollBar}`}
        ref={formContainerRef}
        onScroll={onScroll}
        style={{
          boxShadow: scrollDetected && autoExpandOn ? "inset 0px 4px 3px -2px rgba(50, 50, 50, 0.04)" : "none",
        }}
      >
        {autoExpandOn &&
          !Object?.keys(errors).some((error) => prospectiveFieldsToValidate?.includes(error)) &&
          !mediaQuery && (
            <Button
              variant="icon"
              style={{
                left: "370px",
                bottom: "90px",
                position: "fixed",
                padding: "1rem",
                borderRadius: "100%",
                background: trellisPalette[0],
                color: "#FFF",
                animationDuration: "1.6s",
                animationIterationCount: "infinite",
                transformOrigin: "bottom",
              }}
              className={"nextIconAnimation"}
              onPress={() => {
                if (step + 1 < steps.length && formProgressRefs[step + 1].current) {
                  formProgressRefs[step + 1].current.scrollIntoView({
                    behavior: "smooth",
                    block: "nearest",
                  });
                }

                if (step + 1 === steps.length) {
                  document.querySelector("#submit-button") &&
                    document.querySelector("#submit-button").scrollIntoView({
                      behavior: "smooth",
                      block: "nearest",
                    });
                }
              }}
            >
              <i className={`fa ${step + 1 < steps.length ? "fa-chevron-down" : "fa-chevron-down"}`} />
            </Button>
          )}
        {formToRender}
      </div>
    </Flex>
  );
};

export default withMediaQuery("(max-width:600px)")(FormWizard);
